import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Dumbbell,
  ArrowLeft,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  ToggleLeft,
  ToggleRight,
  Youtube,
  FileText,
  Check,
  Calendar,
  User,
  Library,
  Search,
  Layers,
  GripVertical,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LinkButton } from '../components/ui/link-button';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import { ExerciseHistoryModal } from '../components/ExerciseHistoryModal';
import { SubstitutePicker } from '../components/SubstitutePicker';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Exercise Item
const SortableExercise = ({ ex, idx, total, day, onMoveExercise, onUpdateExercise, onDeleteExercise, editingExercise, setEditingExercise, onShowHistory, library = [] }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ex.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`bg-zinc-950/50 border rounded-xl p-4 ${isDragging ? 'border-neon-lime/40 shadow-lg shadow-neon-lime/10' : 'border-white/5'}`}>
      {editingExercise === ex.id ? (
        <ExerciseForm exercise={ex} library={library}
          onSave={(data) => { onUpdateExercise(day.id, ex.id, data); setEditingExercise(null); }}
          onCancel={() => setEditingExercise(null)} />
      ) : (
        <div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-1">
                <Button variant="ghost" size="sm" className="p-1 h-6 w-6" disabled={idx === 0}
                  onClick={() => onMoveExercise(day.id, ex.id, 'up')}><ChevronUp className="w-4 h-4" /></Button>
                <button
                  {...attributes}
                  {...listeners}
                  className="touch-none p-1 rounded bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing"
                  data-testid={`drag-exercise-${ex.id}`}
                >
                  <GripVertical className="w-4 h-4" />
                </button>
                <Button variant="ghost" size="sm" className="p-1 h-6 w-6" disabled={idx === total - 1}
                  onClick={() => onMoveExercise(day.id, ex.id, 'down')}><ChevronDown className="w-4 h-4" /></Button>
              </div>
              <div>
                <h5 className="font-bold text-white">{ex.name}</h5>
                {ex.execution_details && (
                  <p className="text-sm text-neon-lime font-semibold mt-0.5">{ex.execution_details}</p>
                )}
                {ex.youtube_url && <p className="text-xs text-red-400 truncate max-w-[200px]">{ex.youtube_url}</p>}
              </div>
            </div>
            <div className="flex gap-2">
              {onShowHistory && (
                <Button variant="ghost" size="sm" onClick={() => onShowHistory(ex)} className="text-zinc-400 hover:text-neon-lime p-2" data-testid={`admin-history-${ex.id}`} title="Ver evolução de carga">
                  <TrendingUp className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setEditingExercise(ex.id)} className="text-zinc-400 hover:text-neon-lime p-2"><Edit2 className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => onDeleteExercise(day.id, ex.id, ex.name)} className="text-zinc-400 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
          {ex.instructions && (
            <p className="text-xs text-zinc-500 mt-2 ml-10 line-clamp-2">{ex.instructions}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Separate component for Day Item
const DayItem = ({ 
  day, 
  dayIndex, 
  expanded,
  onToggleExpand,
  onToggleEnabled,
  onDeleteDay,
  onUpdateDay,
  onAddExercise,
  onUpdateExercise,
  onDeleteExercise,
  onMoveExercise,
  userId,
  exerciseLibrary,
  onSaveAsWorkout,
  superSeriesList,
  onAddSuperSeries,
  batchMode,
  isSelected,
  onToggleSelect,
  onShowHistory
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: day.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.85 : 1,
  };

  const [editingExercise, setEditingExercise] = useState(null);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [showLibrarySelector, setShowLibrarySelector] = useState(false);
  const [showSuperSeriesSelector, setShowSuperSeriesSelector] = useState(false);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [newExercise, setNewExercise] = useState({ name: '', youtube_url: '', instructions: '', execution_details: '' });

  const exerciseSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleExerciseDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const exercises = day.exercises || [];
    const oldIdx = exercises.findIndex(e => e.id === active.id);
    const newIdx = exercises.findIndex(e => e.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(exercises, oldIdx, newIdx);
    onMoveExercise(day.id, reordered.map(e => e.id));
  };

  const getDayStatus = () => {
    if (!day.start_date && !day.end_date) return { status: 'sem período', color: 'text-zinc-500', bg: 'bg-zinc-800' };
    const today = new Date().toISOString().split('T')[0];
    if (day.start_date && today < day.start_date) return { status: 'agendado', color: 'text-yellow-400', bg: 'bg-yellow-400/20' };
    if (day.end_date && today > day.end_date) return { status: 'encerrado', color: 'text-red-400', bg: 'bg-red-400/20' };
    return { status: 'ativo', color: 'text-neon-lime', bg: 'bg-neon-lime/20' };
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    let videoId = null;
    if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('youtube.com/embed/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/shorts/')) {
      videoId = url.split('youtube.com/shorts/')[1]?.split('?')[0];
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const dayStatus = getDayStatus();
  const exercises = day.exercises || [];

  const handleAddExercise = async () => {
    if (!newExercise.name.trim()) {
      toast.error('Nome do exercício é obrigatório');
      return;
    }
    await onAddExercise(day.id, { ...newExercise, order: exercises.length });
    setShowAddExercise(false);
    setNewExercise({ name: '', youtube_url: '', instructions: '', execution_details: '' });
  };

  const handleAddFromLibrary = async (libraryExercise) => {
    await onAddExercise(day.id, {
      name: libraryExercise.name,
      youtube_url: libraryExercise.youtube_url || '',
      instructions: libraryExercise.instructions || '',
      execution_details: libraryExercise.execution_details || '',
      substitute_ids: libraryExercise.substitute_ids || [],
      order: exercises.length
    });
    setShowLibrarySelector(false);
    setLibrarySearchTerm('');
  };

  const filteredLibraryExercises = exerciseLibrary.filter(ex => 
    ex.name.toLowerCase().includes(librarySearchTerm.toLowerCase()) ||
    (ex.category || '').toLowerCase().includes(librarySearchTerm.toLowerCase())
  );

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`bg-zinc-900/50 border rounded-2xl overflow-hidden ${day.enabled ? 'border-neon-lime/30' : 'border-white/5'} ${isDragging ? 'shadow-2xl shadow-neon-lime/20 ring-2 ring-neon-lime/30' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="p-4 md:p-5 flex items-center justify-between cursor-pointer" onClick={batchMode ? () => onToggleSelect() : onToggleExpand}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {batchMode && (
            <div
              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-neon-lime border-neon-lime' : 'border-zinc-600 hover:border-zinc-400'}`}
              data-testid={`batch-check-${day.id}`}
            >
              {isSelected && <Check className="w-4 h-4 text-black" />}
            </div>
          )}
          <button
            {...attributes}
            {...listeners}
            className="touch-none p-1.5 rounded-lg bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-500 hover:text-zinc-300 transition-colors cursor-grab active:cursor-grabbing flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
            data-testid={`drag-handle-${day.id}`}
          >
            <GripVertical className="w-5 h-5" />
          </button>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-primary font-black text-xl flex-shrink-0 ${day.enabled ? 'bg-neon-lime text-black' : 'bg-zinc-800 text-zinc-500'}`}>
            {dayIndex + 1}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-primary font-bold text-lg md:text-xl text-white uppercase truncate">
              {day.name || `Treino ${dayIndex + 1}`}
            </h3>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className="text-sm text-zinc-500">{exercises.length} exercício(s)</span>
              {(day.start_date || day.end_date) && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${dayStatus.bg} ${dayStatus.color}`}>
                  {dayStatus.status}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button onClick={(e) => { e.stopPropagation(); onToggleEnabled(day.id, day.enabled); }}
            className={day.enabled ? 'bg-neon-lime/20 text-neon-lime' : 'bg-zinc-800 text-zinc-400'}>
            {day.enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
          </Button>
          <Button onClick={(e) => { e.stopPropagation(); onDeleteDay(day.id, day.name); }} variant="ghost" className="text-zinc-500 hover:text-red-500 p-2">
            <Trash2 className="w-5 h-5" />
          </Button>
          {expanded ? <ChevronUp className="w-6 h-6 text-zinc-400" /> : <ChevronDown className="w-6 h-6 text-zinc-400" />}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 md:px-5 pb-5 border-t border-white/5 pt-4 space-y-4">
              {/* Settings */}
              <div className="bg-zinc-950/50 rounded-xl p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Nome</label>
                  <Input type="text" defaultValue={day.name} className="bg-zinc-900 border-zinc-800 text-white h-10"
                    onBlur={(e) => e.target.value !== day.name && onUpdateDay(day.id, { name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Início</label>
                    <Input type="date" defaultValue={day.start_date || ''} className="bg-zinc-900 border-zinc-800 text-white h-10"
                      onBlur={(e) => onUpdateDay(day.id, { start_date: e.target.value || null })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Término</label>
                    <Input type="date" defaultValue={day.end_date || ''} className="bg-zinc-900 border-zinc-800 text-white h-10"
                      onBlur={(e) => onUpdateDay(day.id, { end_date: e.target.value || null })} />
                  </div>
                </div>
                {exercises.length > 0 && (
                  <Button 
                    onClick={() => onSaveAsWorkout(day)}
                    className="w-full bg-neon-purple/20 text-neon-purple border border-neon-purple/30 hover:bg-neon-purple/30"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salvar como Treino
                  </Button>
                )}
              </div>

              {/* Exercises */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-zinc-400 uppercase">Exercícios</h4>
                {exercises.length === 0 && <p className="text-zinc-600 text-center py-4">Nenhum exercício</p>}
                
                <DndContext sensors={exerciseSensors} collisionDetection={closestCenter} onDragEnd={handleExerciseDragEnd}>
                  <SortableContext items={exercises.map(e => e.id)} strategy={verticalListSortingStrategy}>
                    {exercises.map((ex, idx) => (
                      <SortableExercise
                        key={ex.id}
                        ex={ex}
                        idx={idx}
                        total={exercises.length}
                        day={day}
                        onMoveExercise={onMoveExercise}
                        onUpdateExercise={onUpdateExercise}
                        onDeleteExercise={onDeleteExercise}
                        editingExercise={editingExercise}
                        setEditingExercise={setEditingExercise}
                        onShowHistory={onShowHistory}
                        library={exerciseLibrary}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {showAddExercise ? (
                  <div className="bg-neon-lime/5 border border-neon-lime/30 rounded-xl p-4 space-y-3">
                    <Input value={newExercise.name} onChange={(e) => setNewExercise({...newExercise, name: e.target.value})} placeholder="Nome do exercício" className="bg-zinc-950/50 border-zinc-800 text-white" />
                    <Input value={newExercise.execution_details} onChange={(e) => setNewExercise({...newExercise, execution_details: e.target.value})} placeholder="Execução (ex: 3x12, 4x10 drop-set)" className="bg-zinc-950/50 border-zinc-800 text-white" />
                    <Input value={newExercise.youtube_url} onChange={(e) => setNewExercise({...newExercise, youtube_url: e.target.value})} placeholder="Link YouTube" className="bg-zinc-950/50 border-zinc-800 text-white" />
                    <textarea value={newExercise.instructions} onChange={(e) => setNewExercise({...newExercise, instructions: e.target.value})} placeholder="Instruções" className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-lg p-3 min-h-[60px]" />
                    <div className="flex gap-2">
                      <Button onClick={handleAddExercise} className="flex-1 bg-neon-lime text-black font-bold"><Check className="w-4 h-4 mr-2" />Adicionar</Button>
                      <Button onClick={() => setShowAddExercise(false)} className="bg-zinc-800 text-white"><X className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ) : showLibrarySelector ? (
                  <div className="bg-neon-purple/5 border border-neon-purple/30 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-bold text-white flex items-center gap-2">
                        <Library className="w-5 h-5 text-neon-purple" />
                        Selecionar da Biblioteca
                      </h5>
                      <Button onClick={() => setShowLibrarySelector(false)} variant="ghost" className="p-1 text-zinc-400 hover:text-white">
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input 
                        value={librarySearchTerm} 
                        onChange={(e) => setLibrarySearchTerm(e.target.value)} 
                        placeholder="Buscar exercício..." 
                        className="pl-10 bg-zinc-950/50 border-zinc-800 text-white" 
                      />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto space-y-2">
                      {filteredLibraryExercises.length === 0 ? (
                        <p className="text-zinc-500 text-center py-4">
                          {exerciseLibrary.length === 0 
                            ? 'Nenhum exercício na biblioteca. Adicione no painel Admin.' 
                            : 'Nenhum exercício encontrado'}
                        </p>
                      ) : (
                        filteredLibraryExercises.map((libEx) => (
                          <div 
                            key={libEx.id}
                            onClick={() => handleAddFromLibrary(libEx)}
                            className="bg-zinc-950/50 border border-white/5 rounded-lg p-3 cursor-pointer hover:border-neon-purple/50 hover:bg-zinc-900/50 transition-all"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h6 className="font-bold text-white">{libEx.name}</h6>
                                <div className="flex items-center gap-2 mt-1">
                                  {libEx.category && (
                                    <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full">
                                      {libEx.category}
                                    </span>
                                  )}
                                  {libEx.youtube_url && (
                                    <span className="text-xs text-red-400 flex items-center gap-1">
                                      <Youtube className="w-3 h-3" /> Com vídeo
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Plus className="w-5 h-5 text-neon-purple flex-shrink-0" />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <Button onClick={() => setShowLibrarySelector(true)} className="flex-1 bg-neon-purple/20 text-neon-purple border border-neon-purple/30 hover:bg-neon-purple/30">
                        <Library className="w-5 h-5 mr-2" />Da Biblioteca
                      </Button>
                      <Button onClick={() => setShowAddExercise(true)} className="flex-1 bg-zinc-800 text-white border border-dashed border-zinc-600">
                        <Plus className="w-5 h-5 mr-2" />Criar Novo
                      </Button>
                    </div>
                    {superSeriesList && superSeriesList.length > 0 && (
                      <Button 
                        onClick={() => setShowSuperSeriesSelector(true)} 
                        className="w-full bg-gradient-to-r from-neon-purple/20 to-neon-lime/20 text-white border border-neon-purple/30 hover:from-neon-purple/30 hover:to-neon-lime/30"
                      >
                        <Layers className="w-5 h-5 mr-2" />Adicionar Super Série
                      </Button>
                    )}
                    
                    {/* Super Series Selector */}
                    {showSuperSeriesSelector && (
                      <div className="bg-zinc-950 border border-neon-purple/30 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h5 className="font-bold text-white text-sm">Selecionar Super Série</h5>
                          <button onClick={() => setShowSuperSeriesSelector(false)} className="text-zinc-500 hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {superSeriesList.map(series => (
                            <button
                              key={series.id}
                              onClick={() => {
                                onAddSuperSeries(day.id, series);
                                setShowSuperSeriesSelector(false);
                              }}
                              className="w-full text-left p-3 bg-zinc-900 hover:bg-zinc-800 rounded-lg transition-colors"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-bold text-white">{series.name}</span>
                                <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full">
                                  {series.method_name}
                                </span>
                              </div>
                              <div className="text-xs text-zinc-400">
                                {series.exercises?.map((ex, i) => (
                                  <span key={i}>
                                    {i > 0 && ' + '}
                                    {ex.name}
                                  </span>
                                ))}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ExerciseForm = ({ exercise, onSave, onCancel, library = [] }) => {
  const [data, setData] = useState({ 
    name: exercise?.name || '', 
    youtube_url: exercise?.youtube_url || '', 
    instructions: exercise?.instructions || '',
    execution_details: exercise?.execution_details || '',
    substitute_ids: exercise?.substitute_ids || []
  });
  return (
    <div className="space-y-3">
      <Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} placeholder="Nome" className="bg-zinc-900 border-zinc-700 text-white" />
      <Input value={data.execution_details} onChange={(e) => setData({...data, execution_details: e.target.value})} placeholder="Execução (ex: 3x12, 4x10 drop-set)" className="bg-zinc-900 border-zinc-700 text-white" />
      <Input value={data.youtube_url} onChange={(e) => setData({...data, youtube_url: e.target.value})} placeholder="YouTube URL" className="bg-zinc-900 border-zinc-700 text-white" />
      <textarea value={data.instructions} onChange={(e) => setData({...data, instructions: e.target.value})} placeholder="Instruções" className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-lg p-3 min-h-[60px]" />
      <SubstitutePicker
        selectedIds={data.substitute_ids}
        onChange={(ids) => setData({ ...data, substitute_ids: ids })}
        library={library}
        currentExerciseName={data.name}
      />
      <div className="flex gap-2">
        <Button onClick={() => onSave(data)} className="flex-1 bg-neon-lime text-black font-bold"><Save className="w-4 h-4 mr-2" />Salvar</Button>
        <Button onClick={onCancel} className="bg-zinc-800 text-white"><X className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const UserWorkoutManager = ({ user: adminUser }) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [showAddDay, setShowAddDay] = useState(false);
  const [newDay, setNewDay] = useState({ name: '', start_date: '', end_date: '' });
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [superSeriesList, setSuperSeriesList] = useState([]);
  
  // Saved workouts state
  const [savedWorkouts, setSavedWorkouts] = useState([]);
  const [showSavedWorkoutSelector, setShowSavedWorkoutSelector] = useState(false);
  const [selectedSavedWorkout, setSelectedSavedWorkout] = useState(null);
  const [savedWorkoutSearchTerm, setSavedWorkoutSearchTerm] = useState('');
  const [showSaveAsWorkout, setShowSaveAsWorkout] = useState(null); // day id to save
  const [saveWorkoutName, setSaveWorkoutName] = useState('');
  const [saveWorkoutCategory, setSaveWorkoutCategory] = useState('');

  // Batch date state
  const [batchMode, setBatchMode] = useState(false);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [batchStartDate, setBatchStartDate] = useState('');
  const [batchEndDate, setBatchEndDate] = useState('');
  const [applyingBatch, setApplyingBatch] = useState(false);

  // Exercise load history modal (admin view of student progress)
  const [historyExercise, setHistoryExercise] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      // Buscar dados em paralelo, mas tratar erros individualmente
      const [usersRes, planRes, libraryRes, savedWorkoutsRes, superSeriesRes] = await Promise.allSettled([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/users/${userId}/workout-plan`),
        axios.get(`${API}/admin/exercises`),
        axios.get(`${API}/admin/saved-workouts`),
        axios.get(`${API}/admin/super-series`)
      ]);
      
      // Processar resultados individualmente
      if (usersRes.status === 'fulfilled') {
        setUserData(usersRes.value.data.find(u => u.id === userId));
      }
      if (planRes.status === 'fulfilled') {
        setWorkoutPlan(planRes.value.data);
      }
      if (libraryRes.status === 'fulfilled') {
        setExerciseLibrary(libraryRes.value.data);
      }
      if (savedWorkoutsRes.status === 'fulfilled') {
        setSavedWorkouts(savedWorkoutsRes.value.data || []);
      } else {
        console.error('Erro ao carregar treinos salvos:', savedWorkoutsRes.reason);
        setSavedWorkouts([]);
      }
      if (superSeriesRes.status === 'fulfilled') {
        setSuperSeriesList(superSeriesRes.value.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addDay = async () => {
    if (!newDay.name.trim()) { toast.error('Nome obrigatório'); return; }
    try {
      await axios.post(`${API}/admin/users/${userId}/workout-plan/days`, { ...newDay, enabled: true });
      toast.success('Treino adicionado!');
      setShowAddDay(false);
      setNewDay({ name: '', start_date: '', end_date: '' });
      fetchData();
    } catch (e) { toast.error('Erro ao adicionar'); }
  };

  const deleteDay = async (dayId, name) => {
    if (!window.confirm(`Excluir "${name}"?`)) return;
    try { await axios.delete(`${API}/admin/users/${userId}/workout-plan/days/${dayId}`); toast.success('Excluído'); fetchData(); }
    catch (e) { toast.error('Erro'); }
  };

  const updateDay = async (dayId, data) => {
    try { await axios.patch(`${API}/admin/users/${userId}/workout-plan/days/${dayId}`, data); fetchData(); }
    catch (e) { toast.error('Erro'); }
  };

  const toggleDayEnabled = async (dayId, enabled) => {
    try { await axios.patch(`${API}/admin/users/${userId}/workout-plan/days/${dayId}`, { enabled: !enabled }); fetchData(); }
    catch (e) { toast.error('Erro'); }
  };

  const toggleDaySelection = (dayId) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayId)) next.delete(dayId);
      else next.add(dayId);
      return next;
    });
  };

  const selectAllDays = () => {
    if (selectedDays.size === days.length) {
      setSelectedDays(new Set());
    } else {
      setSelectedDays(new Set(days.map(d => d.id)));
    }
  };

  const applyBatchDates = async () => {
    if (selectedDays.size === 0) { toast.error('Selecione pelo menos um treino'); return; }
    if (!batchStartDate && !batchEndDate) { toast.error('Defina pelo menos uma data'); return; }
    setApplyingBatch(true);
    try {
      const data = {};
      if (batchStartDate) data.start_date = batchStartDate;
      if (batchEndDate) data.end_date = batchEndDate;
      for (const dayId of selectedDays) {
        await axios.patch(`${API}/admin/users/${userId}/workout-plan/days/${dayId}`, data);
      }
      toast.success(`Datas aplicadas em ${selectedDays.size} treino(s)!`);
      setBatchMode(false);
      setSelectedDays(new Set());
      setBatchStartDate('');
      setBatchEndDate('');
      fetchData();
    } catch (e) {
      toast.error('Erro ao aplicar datas');
    } finally {
      setApplyingBatch(false);
    }
  };

  // Drag & Drop sensors with activation delay to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = days.findIndex(d => d.id === active.id);
    const newIndex = days.findIndex(d => d.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic update
    const newDays = arrayMove(days, oldIndex, newIndex);
    setWorkoutPlan(prev => ({ ...prev, days: newDays }));

    try {
      await axios.put(`${API}/admin/users/${userId}/workout-plan/days/reorder`, {
        day_ids: newDays.map(d => d.id)
      });
      toast.success('Ordem atualizada!');
    } catch (e) {
      toast.error('Erro ao reordenar');
      fetchData(); // Rollback
    }
  };

  const addExercise = async (dayId, data) => {
    try { await axios.post(`${API}/admin/users/${userId}/workout-plan/days/${dayId}/exercises`, data); toast.success('Adicionado'); fetchData(); }
    catch (e) { toast.error('Erro'); }
  };

  const updateExercise = async (dayId, exId, data) => {
    try { await axios.patch(`${API}/admin/users/${userId}/workout-plan/days/${dayId}/exercises/${exId}`, data); fetchData(); }
    catch (e) { toast.error('Erro'); }
  };

  const deleteExercise = async (dayId, exId, name) => {
    if (!window.confirm(`Excluir "${name}"?`)) return;
    try { await axios.delete(`${API}/admin/users/${userId}/workout-plan/days/${dayId}/exercises/${exId}`); fetchData(); }
    catch (e) { toast.error('Erro'); }
  };

  const moveExercise = async (dayId, exIdOrIds, dir) => {
    const day = workoutPlan?.days?.find(d => d.id === dayId);
    if (!day) return;

    // If called with array of IDs (from drag & drop)
    if (Array.isArray(exIdOrIds)) {
      try {
        await axios.put(`${API}/admin/users/${userId}/workout-plan/days/${dayId}/exercises/reorder`, { exercise_ids: exIdOrIds });
        fetchData();
        toast.success('Ordem atualizada!');
      } catch (e) { toast.error('Erro'); }
      return;
    }

    // Legacy up/down arrows
    const exs = [...(day.exercises || [])];
    const idx = exs.findIndex(e => e.id === exIdOrIds);
    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= exs.length) return;
    [exs[idx], exs[newIdx]] = [exs[newIdx], exs[idx]];
    try { await axios.put(`${API}/admin/users/${userId}/workout-plan/days/${dayId}/exercises/reorder`, { exercise_ids: exs.map(e => e.id) }); fetchData(); }
    catch (e) { toast.error('Erro'); }
  };

  // Apply saved workout to user
  const applySavedWorkout = async (savedWorkout, customData = {}) => {
    try {
      const response = await axios.post(`${API}/admin/saved-workouts/${savedWorkout.id}/apply/${userId}`, {
        name: customData.name || savedWorkout.name,
        start_date: customData.start_date || null,
        end_date: customData.end_date || null,
        exercises: customData.exercises || savedWorkout.exercises
      });
      toast.success('Treino aplicado com sucesso!');
      setShowSavedWorkoutSelector(false);
      setSelectedSavedWorkout(null);
      fetchData();
    } catch (e) {
      toast.error('Erro ao aplicar treino');
    }
  };

  // Save day as saved workout
  const saveDayAsWorkout = async (day) => {
    if (!saveWorkoutName.trim()) {
      toast.error('Nome do treino é obrigatório');
      return;
    }
    try {
      await axios.post(`${API}/admin/saved-workouts`, {
        name: saveWorkoutName,
        description: `Treino salvo a partir de ${day.name}`,
        category: saveWorkoutCategory || null,
        exercises: day.exercises.map((ex, i) => ({
          name: ex.name,
          youtube_url: ex.youtube_url,
          instructions: ex.instructions,
          execution_details: ex.execution_details,
          order: i,
          is_super_series: ex.is_super_series || false,
          super_series_id: ex.super_series_id,
          super_series_name: ex.super_series_name || ex.name,
          super_series_method: ex.method_name,
          super_series_exercises: ex.sub_exercises,
          rest_between_exercises: ex.rest_between_exercises,
          rest_after_series: ex.rest_after_series
        }))
      });
      toast.success('Treino salvo com sucesso!');
      setShowSaveAsWorkout(null);
      setSaveWorkoutName('');
      setSaveWorkoutCategory('');
      fetchData();
    } catch (e) {
      toast.error('Erro ao salvar treino');
    }
  };

  // Add super series to day
  const addSuperSeriesToDay = async (dayId, superSeries) => {
    const day = workoutPlan.days.find(d => d.id === dayId);
    if (!day) return;

    // Create a single exercise that represents the super series
    const superSeriesExercise = {
      name: `${superSeries.method_name}: ${superSeries.name}`,
      youtube_url: null,
      instructions: superSeries.notes || '',
      execution_details: superSeries.exercises.map(ex => `${ex.name}${ex.execution_details ? ` (${ex.execution_details})` : ''}`).join(' + '),
      order: day.exercises?.length || 0,
      is_super_series: true,
      super_series_id: superSeries.id,
      method_name: superSeries.method_name,
      sub_exercises: superSeries.exercises,
      rest_between_exercises: superSeries.rest_between_exercises,
      rest_after_series: superSeries.rest_after_series
    };

    try {
      await axios.post(`${API}/admin/users/${userId}/workout-plan/days/${dayId}/exercises`, superSeriesExercise);
      toast.success(`Super série "${superSeries.name}" adicionada!`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao adicionar super série');
    }
  };

  const filteredSavedWorkouts = savedWorkouts.filter(w => 
    w.name.toLowerCase().includes(savedWorkoutSearchTerm.toLowerCase())
  );

  if (adminUser?.role !== 'admin') return <div className="flex items-center justify-center min-h-screen"><p className="text-white">Acesso negado</p></div>;
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-lime" /></div>;

  const days = workoutPlan?.days || [];

  return (
    <div className="min-h-screen bg-[#050505] pb-24 px-4 md:px-6 pt-6 md:pt-8">
      <div className="flex items-center gap-4 mb-6">
        <LinkButton to="/admin/users-workouts" variant="ghost" className="text-zinc-400 hover:text-white p-2"><ArrowLeft className="w-6 h-6" /></LinkButton>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-neon-lime rounded-full flex items-center justify-center"><User className="w-5 h-5 text-black" /></div>
          <div>
            <h1 className="font-primary font-black text-2xl text-white uppercase">{userData?.name || 'Aluno'}</h1>
            <p className="text-zinc-500 text-sm">{userData?.email}</p>
          </div>
        </div>
      </div>

      {!showAddDay && !showSavedWorkoutSelector ? (
        <div className="flex gap-3 mb-6">
          <Button onClick={() => setShowAddDay(true)} className="flex-1 bg-neon-lime text-black font-bold h-14 text-lg">
            <Plus className="w-6 h-6 mr-2" />Novo Treino
          </Button>
          <Button onClick={() => setShowSavedWorkoutSelector(true)} className="flex-1 bg-neon-purple text-white font-bold h-14 text-lg">
            <Library className="w-6 h-6 mr-2" />Usar Treino
          </Button>
          {days.length > 0 && (
            <Button
              onClick={() => { setBatchMode(!batchMode); setSelectedDays(new Set()); }}
              className={`h-14 px-4 font-bold ${batchMode ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-white hover:bg-zinc-700'}`}
              data-testid="batch-date-btn"
            >
              <Calendar className="w-5 h-5" />
            </Button>
          )}
        </div>
      ) : showSavedWorkoutSelector ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-zinc-900/50 border border-neon-purple/30 rounded-2xl p-5 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-white flex items-center gap-2">
              <Library className="w-5 h-5 text-neon-purple" />
              Usar Treino Salvo
            </h3>
            <Button onClick={() => { setShowSavedWorkoutSelector(false); setSelectedSavedWorkout(null); }} variant="ghost" className="text-zinc-400 hover:text-white p-2">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {!selectedSavedWorkout ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input 
                  value={savedWorkoutSearchTerm} 
                  onChange={(e) => setSavedWorkoutSearchTerm(e.target.value)} 
                  placeholder="Buscar treino salvo..." 
                  className="pl-10 bg-zinc-950/50 border-zinc-800 text-white" 
                />
              </div>
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {filteredSavedWorkouts.length === 0 ? (
                  <div className="text-center py-8">
                    <Library className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500">
                      {savedWorkouts.length === 0 
                        ? 'Nenhum treino salvo. Crie treinos salvos no Painel Admin.' 
                        : 'Nenhum treino encontrado'}
                    </p>
                  </div>
                ) : (
                  filteredSavedWorkouts.map((workout) => (
                    <div 
                      key={workout.id}
                      onClick={() => setSelectedSavedWorkout(workout)}
                      className="bg-zinc-950/50 border border-white/5 rounded-lg p-4 cursor-pointer hover:border-neon-purple/50 hover:bg-zinc-900/50 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-bold text-white">{workout.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            {workout.category && (
                              <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full">
                                {workout.category}
                              </span>
                            )}
                            <span className="text-xs text-zinc-500">
                              <Dumbbell className="w-3 h-3 inline mr-1" />
                              {workout.exercises?.length || 0} exercício(s)
                            </span>
                          </div>
                        </div>
                        <ChevronDown className="w-5 h-5 text-neon-purple flex-shrink-0 rotate-[-90deg]" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="bg-zinc-950/50 border border-neon-purple/30 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-white text-lg">{selectedSavedWorkout.name}</h4>
                    {selectedSavedWorkout.category && (
                      <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full">
                        {selectedSavedWorkout.category}
                      </span>
                    )}
                  </div>
                  <Button onClick={() => setSelectedSavedWorkout(null)} variant="ghost" className="text-zinc-400 hover:text-white p-1">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-zinc-400">Exercícios:</p>
                  {selectedSavedWorkout.exercises?.map((ex, idx) => (
                    <div key={idx} className="text-sm text-zinc-300 flex items-center gap-2">
                      <span className="text-neon-purple font-bold">{idx + 1}.</span>
                      {ex.name}
                      {ex.youtube_url && <Youtube className="w-3 h-3 text-red-400" />}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Nome personalizado (opcional)</label>
                <Input 
                  placeholder={selectedSavedWorkout.name}
                  className="bg-zinc-950/50 border-zinc-800 text-white"
                  id="custom-workout-name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Data início</label>
                  <Input type="date" className="bg-zinc-950/50 border-zinc-800 text-white" id="custom-start-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Data fim</label>
                  <Input type="date" className="bg-zinc-950/50 border-zinc-800 text-white" id="custom-end-date" />
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => applySavedWorkout(selectedSavedWorkout, {
                    name: document.getElementById('custom-workout-name')?.value || selectedSavedWorkout.name,
                    start_date: document.getElementById('custom-start-date')?.value || null,
                    end_date: document.getElementById('custom-end-date')?.value || null
                  })}
                  className="flex-1 bg-neon-purple text-white font-bold"
                >
                  <Check className="w-5 h-5 mr-2" />
                  Aplicar Treino
                </Button>
                <Button onClick={() => setSelectedSavedWorkout(null)} className="bg-zinc-800 text-white">
                  Voltar
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-zinc-900/50 border border-neon-lime/30 rounded-2xl p-5 mb-6 space-y-4">
          <h3 className="font-bold text-white">Novo Treino</h3>
          <Input value={newDay.name} onChange={(e) => setNewDay({...newDay, name: e.target.value})} placeholder="Nome do treino" className="bg-zinc-950/50 border-zinc-800 text-white h-12" />
          <div className="grid grid-cols-2 gap-4">
            <Input type="date" value={newDay.start_date} onChange={(e) => setNewDay({...newDay, start_date: e.target.value})} className="bg-zinc-950/50 border-zinc-800 text-white h-12" />
            <Input type="date" value={newDay.end_date} onChange={(e) => setNewDay({...newDay, end_date: e.target.value})} className="bg-zinc-950/50 border-zinc-800 text-white h-12" />
          </div>
          <div className="flex gap-3">
            <Button onClick={addDay} className="flex-1 bg-neon-lime text-black font-bold"><Check className="w-5 h-5 mr-2" />Criar</Button>
            <Button onClick={() => setShowAddDay(false)} className="bg-zinc-800 text-white"><X className="w-5 h-5" /></Button>
          </div>
        </motion.div>
      )}

      {days.length === 0 ? (
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 text-center">
          <Dumbbell className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="font-bold text-xl text-white mb-2">Nenhum treino</h2>
          <p className="text-zinc-500">Adicione o primeiro treino para {userData?.name}.</p>
        </div>
      ) : (
        <>
        {batchMode && (
          <motion.div
            className="bg-zinc-900 border border-yellow-500/30 rounded-2xl p-4 mb-4 sticky top-0 z-30"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-yellow-400" />
                <span className="font-bold text-white">Aplicar Datas em Lote</span>
                <span className="text-xs text-zinc-500">({selectedDays.size} selecionado{selectedDays.size !== 1 ? 's' : ''})</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={selectAllDays} className="text-xs text-neon-lime hover:underline">
                  {selectedDays.size === days.length ? 'Desmarcar todos' : 'Selecionar todos'}
                </button>
                <Button onClick={() => { setBatchMode(false); setSelectedDays(new Set()); }} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs text-zinc-400 mb-1">Data de Início</label>
                <DatePicker
                  value={batchStartDate}
                  onChange={setBatchStartDate}
                  placeholder="Selecionar início"
                  data-testid="batch-start-date"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs text-zinc-400 mb-1">Data de Vencimento</label>
                <DatePicker
                  value={batchEndDate}
                  onChange={setBatchEndDate}
                  placeholder="Selecionar vencimento"
                  data-testid="batch-end-date"
                />
              </div>
              <Button
                onClick={applyBatchDates}
                disabled={applyingBatch || selectedDays.size === 0 || (!batchStartDate && !batchEndDate)}
                className="bg-yellow-500 text-black font-bold hover:bg-yellow-400 disabled:opacity-50 h-10"
                data-testid="apply-batch-dates"
              >
                {applyingBatch ? 'Aplicando...' : `Aplicar em ${selectedDays.size} treino(s)`}
              </Button>
            </div>
          </motion.div>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={days.map(d => d.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {days.map((day, idx) => (
                <DayItem
                  key={day.id}
                  day={day}
                  dayIndex={idx}
                  expanded={expandedDay === day.id}
                  onToggleExpand={() => setExpandedDay(expandedDay === day.id ? null : day.id)}
                  onToggleEnabled={toggleDayEnabled}
                  onDeleteDay={deleteDay}
                  onUpdateDay={updateDay}
                  onAddExercise={addExercise}
                  onUpdateExercise={updateExercise}
                  onDeleteExercise={deleteExercise}
                  onMoveExercise={moveExercise}
                  userId={userId}
                  exerciseLibrary={exerciseLibrary}
                  superSeriesList={superSeriesList}
                  batchMode={batchMode}
                  isSelected={selectedDays.has(day.id)}
                  onToggleSelect={() => toggleDaySelection(day.id)}
                  onAddSuperSeries={addSuperSeriesToDay}
                  onSaveAsWorkout={(day) => {
                    setShowSaveAsWorkout(day);
                    setSaveWorkoutName(day.name);
                  }}
                  onShowHistory={(ex) => setHistoryExercise({ id: ex.id, name: ex.name })}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        </>
      )}

      {/* Save as Workout Modal */}
      {showSaveAsWorkout && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-zinc-900 border border-neon-purple/30 rounded-2xl p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-xl text-white">Salvar como Treino</h3>
              <Button onClick={() => setShowSaveAsWorkout(null)} variant="ghost" className="text-zinc-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-zinc-400 text-sm mb-4">
              Salve este treino para reutilizar com outros alunos
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Nome do Treino *</label>
                <Input
                  value={saveWorkoutName}
                  onChange={(e) => setSaveWorkoutName(e.target.value)}
                  placeholder="Ex: Treino A - Peito"
                  className="bg-zinc-950 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Categoria</label>
                <Input
                  value={saveWorkoutCategory}
                  onChange={(e) => setSaveWorkoutCategory(e.target.value)}
                  placeholder="Ex: Peito, Full Body..."
                  className="bg-zinc-950 border-zinc-700 text-white"
                />
              </div>
              <div className="bg-zinc-950 rounded-lg p-3">
                <p className="text-xs text-zinc-500 mb-2">Exercícios ({showSaveAsWorkout.exercises?.length || 0}):</p>
                {showSaveAsWorkout.exercises?.map((ex, idx) => (
                  <p key={idx} className="text-sm text-zinc-300">{idx + 1}. {ex.name}</p>
                ))}
              </div>
              <div className="flex gap-3">
                <Button onClick={() => saveDayAsWorkout(showSaveAsWorkout)} className="flex-1 bg-neon-purple text-white font-bold">
                  <Save className="w-5 h-5 mr-2" />
                  Salvar Treino
                </Button>
                <Button onClick={() => setShowSaveAsWorkout(null)} className="bg-zinc-800 text-white">
                  Cancelar
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Exercise Load History Modal (Admin) */}
      <ExerciseHistoryModal
        open={!!historyExercise}
        exerciseId={historyExercise?.id}
        exerciseName={historyExercise?.name || ''}
        userId={userId}
        onClose={() => setHistoryExercise(null)}
      />
    </div>
  );
};
